import { cn } from "@newxsystems/shared";
import { TootActions } from "../../../../modules/layout/components/TootActions/TootActions";
import { TootHeading } from "../../../../modules/layout/components/TootHeading/TootHeading";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import { TootMediaFrame } from "../../../../modules/surface/components/TootMediaFrame/TootMediaFrame";
import type { ICallToActionBlockProps } from "./CallToActionBlock.props";

export const CallToActionBlock = ({
  anchorId,
  buttons,
  direction,
  eyebrow,
  heading,
  image,
  parentClass,
  spacing,
  subheading,
  variant,
}: ICallToActionBlockProps) => {
  const isHorizontal = variant === "horizontal";
  const actions = buttons ?? [];
  const sectionId = anchorId;
  const imageSrc = image?.defaultSrc;
  const hasImage = Boolean(imageSrc);
  const isReverse = direction !== "ltr";
  const hasCopy = Boolean(eyebrow || heading || subheading || actions.length);

  if (!hasCopy && !hasImage) {
    return null;
  }

  return (
    <TootSectionShell
      className="relative"
      id={sectionId ?? undefined}
      rootClassName={cn("toot-call-to-action-block", parentClass)}
      spacing={spacing ?? undefined}
      tone="dark"
      width="wide"
    >
      <div className={cn("toot-heat-panel min-h-96", !isHorizontal && "min-h-0 text-center")}>
        <div
          className={cn(
            "relative z-[2] grid gap-[clamp(1.5rem,3vw,2.75rem)] p-[clamp(1.25rem,3vw,2.5rem)]",
            isHorizontal && hasImage
              ? "lg:grid-cols-[minmax(0,1.08fr)_minmax(20rem,0.78fr)] lg:items-center"
              : "justify-items-center gap-[clamp(1.5rem,3vw,2.5rem)] p-[clamp(1.5rem,4.5vw,4.25rem)]",
          )}
        >
          <div
            className={cn(
              "flex w-full max-w-none flex-col justify-center gap-6",
              !isHorizontal &&
                "w-[min(100%,62rem)] max-w-[62rem] items-center gap-[clamp(1.15rem,2vw,1.5rem)]",
              isHorizontal && hasImage && isReverse && "lg:order-2",
            )}
          >
            {isHorizontal && eyebrow ? (
              <div className="inline-flex w-fit items-center rounded-full border border-toot-orange/40 bg-toot-surface-raised/80 px-3 py-1.5 text-[0.6875rem] font-bold uppercase leading-[1.2] tracking-[0.16em] text-toot-orange">
                <span>{eyebrow}</span>
              </div>
            ) : null}

            <TootHeading
              alignment={isHorizontal ? "left" : "center"}
              className={!isHorizontal ? "max-w-[min(100%,60rem)]" : undefined}
              eyebrow={isHorizontal ? undefined : eyebrow}
              heading={heading}
              microLabel={undefined}
              size="section"
              subheading={subheading}
            />
            <TootActions
              actions={actions}
              alignment={isHorizontal ? "left" : "center"}
              className={cn(
                "pt-1",
                "max-sm:w-full max-sm:[&_.toot-button]:w-full max-sm:[&_.toot-button]:justify-center",
              )}
            />
          </div>

          {hasImage ? (
            <div
              className={cn(
                "min-w-0 w-full",
                !isHorizontal &&
                  "max-w-[min(100%,55rem)] justify-self-center [&_figcaption]:text-left",
                isHorizontal && hasImage && isReverse && "lg:order-1",
              )}
            >
              <TootMediaFrame
                alt={image?.alternateText ?? ""}
                aspectRatio={isHorizontal ? "4 / 3" : "16 / 9"}
                caption={image?.caption}
                className={cn(
                  "toot-media-heat w-full border-toot-orange/30 shadow-2xl shadow-black/50",
                  !isHorizontal && "max-w-4xl",
                )}
                focalPoint={image?.focalPoint ?? undefined}
                height={image?.height ?? undefined}
                sizes={image?.sizes ?? (isHorizontal ? "(min-width: 1024px) 42vw, 100vw" : "100vw")}
                src={imageSrc}
                srcSet={image?.srcSet ?? undefined}
                width={image?.width ?? undefined}
              />
            </div>
          ) : null}
        </div>
      </div>
    </TootSectionShell>
  );
};

CallToActionBlock.displayName = "Block::CallToActionBlock";
